import React, { useState } from 'react'
import Icon from 'components/elements/Icon'

import { CardType } from 'types/deck'

import ToastService from 'services/toast.service'

import { useActionless, useActions, useAppSelector } from 'redux/hooks'
import { SET_COLLECTION_V2_STATE } from 'redux/collectionV2/actions/types'
import { modifyCollectionCardMapAction } from 'redux/collectionV2/actions'

import CollectionService from 'services/collection.service'
import ArchidektDropdown from 'components/elements/ArchidektDropdown'
import MultiEditModal from 'components/collectionV2/forms/multiEdit/Modal'
import SimpleSpinner from 'components/elements/SimpleSpinner'
import AddToOtherDeck from 'components/card/cardForm/addToOtherDeck'
import ConfirmDeleteModal from 'components/elements/ConfirmDeleteModal'

import { getPriceStringFromFloat } from 'utils/Price'
import { getDeckPriceAndInfo } from 'utils/calculateDeckPrice'

import styles from './multiEditControls.module.scss'

const MultiEditControls = () => {
  const canEditDeck = true

  const collectionCards = useAppSelector(state => state.collectionV2.collectionCards)
  const multiSelectedIds = useAppSelector(state => state.collectionV2.multiSelectedIds)
  const priceSource = useAppSelector(state => state.collectionV2.priceSource)

  const [setCollectionV2State] = useActionless(SET_COLLECTION_V2_STATE)
  const [modifyCollectionCardMap] = useActions(modifyCollectionCardMapAction)

  const [editModalOpen, setEditModalOpen] = useState(false)
  const [loading, setLoading] = useState(false)
  const [addToOtherDeckModal, setAddToOtherDeckModal] = useState(false)
  const [removeAllConfirm, setRemoveAllConfirm] = useState(false)

  const handleOnAddedCardsToOtherDeck = () => {
    setCollectionV2State({ multiSelectedIds: {} })
    setAddToOtherDeckModal(false)
  }

  const handleRemoveAll = async () => {
    setLoading(true)

    const removedIds = Object.keys(multiSelectedIds).map(Number)

    try {
      const MAX_UPDATE_SIZE = 25

      for (let i = 0; i < removedIds.length; i += MAX_UPDATE_SIZE) {
        const slicedRemovedIds = removedIds.slice(i, i + MAX_UPDATE_SIZE)

        await CollectionService.deleteCards(slicedRemovedIds)
      }

      modifyCollectionCardMap([], Object.keys(multiSelectedIds).map(Number))
      setCollectionV2State({ multiSelectedIds: {} })

      ToastService.create('Cards removed from collection', 'Collection page', 'success')
    } catch {
      ToastService.create('Failed to remove cards from collection', 'Collection page', 'error')
    } finally {
      setLoading(false)
    }
  }

  const cardsOnly = Object.values(collectionCards).reduce((acc, collectionCard) => {
    acc[collectionCard.id] = { ...collectionCard.card, qty: collectionCard.quantity, modifier: collectionCard.modifier }

    return acc
  }, {} as Record<string, CardType>)

  const selectedCards = Object.keys(multiSelectedIds).map(id => cardsOnly[id])
  const totalSelected = Object.keys(multiSelectedIds).length

  const { estDeckCost } = getDeckPriceAndInfo(selectedCards, {}, [priceSource])
  const estPrice = getPriceStringFromFloat(estDeckCost, priceSource)

  return (
    <>
      <div className={`${styles.bar} ${totalSelected && styles.shown}`}>
        <div className={styles.info}>
          <label>{totalSelected} Card(s) Selected</label>
          <label className={styles.selectedPrice}>Selected price {estPrice}</label>
        </div>
        {!loading && (
          <div className={styles.controls}>
            {canEditDeck && (
              <button className={styles.button} onClick={() => setEditModalOpen(true)}>
                <Icon style={{ marginLeft: '.3rem' }} name="edit" /> <span>Edit</span>
              </button>
            )}

            <button className={styles.button} onClick={() => setCollectionV2State({ multiSelectedIds: {} })}>
              <Icon name="close" /> <span>Unselect</span>
            </button>

            <ArchidektDropdown
              triggerClassName={styles.dropdownTrigger}
              menuClassName={styles.quickOptions}
              defaultTriggerClassName={styles.defaultTrigger}
              direction="up"
              options={[
                { icon: 'trash', label: 'Delete all', onClick: () => setRemoveAllConfirm(true), hidden: !canEditDeck },
                { icon: 'add circle', label: 'Add to deck', onClick: () => setAddToOtherDeckModal(true) },
              ]}
            />
          </div>
        )}

        {loading && (
          <div className={styles.loading}>
            <SimpleSpinner size="medXSmall" /> Saving changes
          </div>
        )}
      </div>

      <ConfirmDeleteModal
        onClose={() => setRemoveAllConfirm(false)}
        open={removeAllConfirm}
        onDelete={handleRemoveAll}
        buttonText="Remove selected collection records"
        delayEnabledTime={3000}
        header="Remove collection records"
        loadingMessage="Removing records..."
        message="Remove all multi-selected collection records? This action cannot be undone."
      />
      <MultiEditModal open={editModalOpen} onClose={() => setEditModalOpen(false)} />
      <AddToOtherDeck
        parentControlled
        cards={selectedCards}
        open={addToOtherDeckModal}
        onClose={() => setAddToOtherDeckModal(false)}
        onAdded={handleOnAddedCardsToOtherDeck}
      />
    </>
  )
}

export default MultiEditControls
